import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { listActivityLogs } from "@/lib/logs.functions";
import { listMembers } from "@/lib/admin.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Inbox, ScrollText, RefreshCw } from "lucide-react";
import { formatDateTimeBR } from "@/lib/datetime";

export const Route = createFileRoute("/_authenticated/admin/logs")({
  head: () => ({ meta: [{ title: "Logs · Admin" }, { name: "robots", content: "noindex" }] }),
  component: LogsPage,
});

const CATEGORIES = ["all", "auth", "user", "admin", "email", "sync", "system", "error"] as const;
const SEVERITIES = ["all", "info", "warn", "error"] as const;

function LogsPage() {
  const fn = useServerFn(listActivityLogs);
  const membersFn = useServerFn(listMembers);
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>("all");
  const [severity, setSeverity] = useState<(typeof SEVERITIES)[number]>("all");
  const [search, setSearch] = useState("");
  const [userId, setUserId] = useState("");
  const [page, setPage] = useState(0);
  const limit = 100;

  const { data: members } = useQuery({
    queryKey: ["members-for-logs"],
    queryFn: () => membersFn(),
    staleTime: 5 * 60_000,
  });

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["activity-logs", category, severity, search, userId, page],
    queryFn: () =>
      fn({
        data: {
          category,
          severity,
          search,
          ...(userId ? { actorUserId: userId } : {}),
          limit,
          offset: page * limit,
        },
      }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-primary sm:text-3xl">
            Logs de atividade
          </h1>
          <p className="mt-1 text-sm text-muted-foreground sm:text-base">
            Registro de ações de usuários, envios de e-mail, sincronizações e erros da plataforma.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={cn("mr-2 h-4 w-4", isFetching && "animate-spin")} /> Atualizar
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto_auto]">
        <Input
          placeholder="Buscar por mensagem, ação ou autor"
          value={search}
          onChange={(e) => {
            setPage(0);
            setSearch(e.target.value);
          }}
        />
        <select
          aria-label="Categoria dos registros"
          value={category}
          onChange={(e) => {
            setPage(0);
            setCategory(e.target.value as any);
          }}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c === "all" ? "Todas categorias" : c}
            </option>
          ))}
        </select>
        <select
          aria-label="Severidade dos registros"
          value={severity}
          onChange={(e) => {
            setPage(0);
            setSeverity(e.target.value as any);
          }}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {s === "all" ? "Todas severidades" : s}
            </option>
          ))}
        </select>
        <select
          aria-label="Filtrar por usuário"
          value={userId}
          onChange={(e) => {
            setPage(0);
            setUserId(e.target.value);
          }}
          className="max-w-[240px] rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">Todos os usuários</option>
          {(members ?? []).map((m: any) => (
            <option key={m.id} value={m.id}>
              {m.full_name ?? m.email ?? m.id}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="h-[70vh] animate-pulse rounded-xl bg-secondary" aria-hidden />
      ) : (data?.rows.length ?? 0) === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <Inbox className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-4 font-medium text-foreground">Nenhum log encontrado</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Ajuste os filtros ou aguarde novas atividades.
          </p>
        </div>
      ) : (
        <div className="max-h-[70vh] overflow-auto rounded-xl border border-border">
          <table className="min-w-full text-sm">
            <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Quando</th>
                <th className="px-3 py-2 text-left">Categoria</th>
                <th className="px-3 py-2 text-left">Ação</th>
                <th className="px-3 py-2 text-left">Autor</th>
                <th className="px-3 py-2 text-left">Mensagem</th>
              </tr>
            </thead>
            <tbody>
              {data!.rows.map((r: any) => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                    {formatDateTimeBR(r.created_at)}
                  </td>
                  <td className="px-3 py-2">
                    <span className="rounded bg-secondary px-2 py-0.5 text-xs font-medium">
                      {r.category}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={cn(
                        "rounded px-2 py-0.5 text-xs font-medium",
                        r.severity === "error" && "bg-destructive/15 text-destructive",
                        r.severity === "warn" && "bg-gold/20 text-gold-strong",
                        r.severity === "info" && "bg-primary/10 text-primary",
                      )}
                    >
                      {r.action}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {r.actor_label ?? r.actor_user_id ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-foreground">
                    <div>{r.message ?? "—"}</div>
                    {r.metadata && (
                      <details className="mt-1 text-xs text-muted-foreground">
                        <summary className="cursor-pointer">metadata</summary>
                        <pre className="mt-1 max-w-xl overflow-x-auto rounded bg-secondary/40 p-2 text-[11px]">
                          {JSON.stringify(r.metadata, null, 2)}
                        </pre>
                      </details>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <ScrollText className="h-4 w-4" />
          {data?.count ?? 0} registros
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!data || (page + 1) * limit >= (data?.count ?? 0)}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
          </Button>
        </div>
      </div>
    </div>
  );
}
